import { type BookingClient, type SchedulingEventType, type SchedulingUser } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { hostIdForClient, type HostSelection } from './eventTypeCoverage'

export type SkipReason = 'noEmail' | 'unsubscribed' | 'noOwner' | 'ownerNotShared' | 'ownerOff'

export interface InvitationRecipient {
  clientId: string
  client: BookingClient
  hostId: string
}

export interface ClientEligibility {
  hostId: string | null
  skipReason: SkipReason | null
}

export interface EligibilityInput {
  ownerIdByClientId: Record<string, string | null>
  unsubscribedClientIds: Set<string>
  hostSelection: HostSelection
  enabledUserIds: Set<string>
}

export const toBookingClient = (user: User): BookingClient => ({
  contactId: user.id,
  firstName: user.firstName || undefined,
  lastName: user.lastName || undefined,
  email: user.email || undefined,
})

const skipReasonFor = (client: User, ownerId: string | null, hostId: string | null, input: EligibilityInput): SkipReason | null => {
  if (!client.email) {
    return 'noEmail'
  }
  if (input.unsubscribedClientIds.has(client.id)) {
    return 'unsubscribed'
  }
  if (hostId === null) {
    return ownerId === null ? 'noOwner' : 'ownerNotShared'
  }
  return input.enabledUserIds.has(hostId) ? null : 'ownerOff'
}

export const clientEligibility = (client: User, input: EligibilityInput): ClientEligibility => {
  const ownerId = input.ownerIdByClientId[client.id] ?? null
  const hostId = hostIdForClient(ownerId, input.hostSelection)
  return { hostId, skipReason: skipReasonFor(client, ownerId, hostId, input) }
}

export const getInvitationRecipients = (clients: User[], input: EligibilityInput): InvitationRecipient[] =>
  clients.flatMap((client) => {
    const { hostId, skipReason } = clientEligibility(client, input)
    return hostId !== null && skipReason === null ? [{ clientId: client.id, client: toBookingClient(client), hostId }] : []
  })

export const hostBookingLink = (host: SchedulingUser | undefined, eventType: SchedulingEventType): string | undefined =>
  host?.publicUrl ? `${host.publicUrl}/${eventType.slug}` : undefined

export interface InvitationSummary {
  invitedCount: number
  noEmailCount: number
  unsubscribedCount: number
  noOwnerCount: number
  ownerNotSharedCount: number
  ownerNotEnabledCount: number
  notFoundCount: number
}

export const getInvitationSummary = (clientIds: string[], clients: User[], input: EligibilityInput): InvitationSummary => {
  const countBySkipReason = clients.reduce<Record<SkipReason | 'invited', number>>(
    (counts, client) => {
      const reason = clientEligibility(client, input).skipReason ?? 'invited'
      counts[reason] += 1
      return counts
    },
    { invited: 0, noEmail: 0, unsubscribed: 0, noOwner: 0, ownerNotShared: 0, ownerOff: 0 },
  )
  const foundIds = new Set(clients.map((client) => client.id))
  return {
    invitedCount: countBySkipReason.invited,
    noEmailCount: countBySkipReason.noEmail,
    unsubscribedCount: countBySkipReason.unsubscribed,
    noOwnerCount: countBySkipReason.noOwner,
    ownerNotSharedCount: countBySkipReason.ownerNotShared,
    ownerNotEnabledCount: countBySkipReason.ownerOff,
    notFoundCount: clientIds.filter((clientId) => !foundIds.has(clientId)).length,
  }
}
